/**
 * belgeKuralService.js — Belge Kuralları (belge_kural)
 *
 * Base URL: {JAVA_BACKEND_API_URL}/projects/belge-kural
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token
 * Kaynak: docs/guides/FE-fuar-kural-sistemi-guide.md Layer C (2026-08-24)
 *
 * Bir standın HANGİ belgeleri yüklemesi gerektiğini artık organizatörün yazdığı
 * bir kural da belirleyebiliyor — eskiden bu sadece project_stand_system
 * konfigürasyonundan geliyordu.
 *
 * Kural = stand tipi + AÇIK bir yarı-açık yükseklik aralığı [hMin, hMax):
 *  - hMin dahil, hMax HARİÇ → [0, 4.00) ile [4.00, 6.00) boşluksuz/çakışmasız döşenir
 *    ve tam 4,00 m'lik bir stand İKİNCİSİNE düşer. (Eski project_stand_system
 *    bantları çakışıyordu — 0.00–3.50 ile 0.00–4.00 aynı tipte — ve bir insanın
 *    seçmesi gerekiyordu; bu aralıklar onun yerine geçiyor.)
 *  - İki uç da null olabilir; İKİSİ BİRDEN null NORMAL DURUM: "Ahşap her yükseklikte
 *    bu belgeleri ister."
 *  - Aynı (fuar, stand tipi) için kesişen iki aralık 409 ile reddedilir ve gövde
 *    çakışılan kuralı adlandırır (err.data'da taşınıyor).
 *  - Stand tipi ağacında miras alır (Layer B ile aynı).
 *  - fuarId null = global kural; dolu = sadece o fuar. Fuar globali yener.
 *
 * ⚠️ ESKİ SİSTEMİ DEĞİŞTİRMEDİ, ÜSTÜNE OTURDU. Kaskad artık 5 seviye:
 *    1. STAND        stand_required_files                    (stand bazlı override)
 *    2. FAIR   yeni  belge_kural, fuarId = bu fuar
 *    3. FAIR   eski  fuar_project_stand_system_required_files
 *    4. GLOBAL yeni  belge_kural, fuarId = null
 *    5. GLOBAL eski  project_stand_system_required_files      → yoksa NONE
 * Her iki fuar seviyesi her iki global seviyenin üstünde: yeni bir GLOBAL kural,
 * birinin eski yolla yaptığı bir FUAR override'ını ezemiyor.
 *
 * ⚠️ Eşleşen ama BOŞ dosya listesi olan kural "sıfır belge" demektir ve kaskadı
 * DURDURUR — eski konfigürasyona düşmez. Bir fuarın "bu stand tipinden hiçbir şey
 * istemiyorum" deme yolu budur. Düşmesini istiyorsanız kuralı SİLİN.
 *
 * ⚠️ Tablolar BOŞ geliyor; bir kural yazılana kadar her proje tam olarak eskisi
 * gibi çözülür, /stand-heights-classic ve /required-files-classic'e dokunulmadı.
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/projects`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 150) => new Promise((r) => setTimeout(r, ms));

/**
 * required-files/effective.level — hangi kaskad seviyesi kazandı.
 * ⚠️ KIRICI DEĞİŞİKLİK: eskiden STAND / FAIR / GLOBAL / NONE idi. Eski dizeleri
 * eşleştiren her kod artık fuar ve global seviyelerini TANIMAZ.
 */
export const REQUIRED_FILES_LEVEL = {
  STAND: 'STAND',
  FAIR_KURAL: 'FAIR_KURAL',
  FAIR_PSS: 'FAIR_PSS',
  GLOBAL_KURAL: 'GLOBAL_KURAL',
  GLOBAL_PSS: 'GLOBAL_PSS',
  NONE: 'NONE',
};

export const REQUIRED_FILES_LEVEL_LABELS = {
  get STAND() { return i18n.t("services:belgeKuralService.standBazliOverride"); },
  get FAIR_KURAL() { return i18n.t("services:belgeKuralService.fuarBelgeKurali"); },
  get FAIR_PSS() { return i18n.t("services:belgeKuralService.fuarKonfigurasyonuEski"); },
  get GLOBAL_KURAL() { return i18n.t("services:belgeKuralService.globalBelgeKurali"); },
  get GLOBAL_PSS() { return i18n.t("services:belgeKuralService.globalKonfigurasyonEski"); },
  get NONE() { return i18n.t("services:belgeKuralService.kuralYok"); },
};

let _mockBelgeKural = [
  {
    id: 1, fuarId: 3, standTypeId: 5, standTypeName: 'Ahşap',
    hMin: null, hMax: 4, aciklama: 'Ahşap, 4 m altı',
    standFileIds: [1, 3],
    standFiles: [
      { stand_file_id: 1, stand_file_name: 'Malzeme Listesi' },
      { stand_file_id: 3, stand_file_name: 'Elektrik Projesi' },
    ],
  },
  {
    id: 2, fuarId: 3, standTypeId: 5, standTypeName: 'Ahşap',
    hMin: 4, hMax: null, aciklama: 'Ahşap, 4 m ve üstü — statik hesap da isteniyor',
    standFileIds: [1, 2, 3],
    standFiles: [
      { stand_file_id: 1, stand_file_name: 'Malzeme Listesi' },
      { stand_file_id: 2, stand_file_name: 'Statik Hesap' },
      { stand_file_id: 3, stand_file_name: 'Elektrik Projesi' },
    ],
  },
  // GLOBAL kurallar (fuarId null) — fuar sekmesindeki "Global kurallardan devralınanlar"
  // bölümünün mock modda üç durumu da göstermesi için (fuar 3'e göre):
  //  · Özel Stand (1) → alt tipi Ahşap'ta fuar kuralı var  → "Kısmen geçersiz kılındı"
  //  · Ahşap (5) [3, 6) → fuar 3'ün iki Ahşap kuralı tamamen örtüyor → "geçersiz kılındı"
  //  · Sistem Stand (2), boş liste → fuar kuralı yok      → "Devralınıyor"
  {
    id: 3, fuarId: null, standTypeId: 1, standTypeName: 'Özel Stand',
    hMin: null, hMax: null, aciklama: 'Özel stand — her yükseklikte temel set',
    standFileIds: [1, 3, 4],
    standFiles: [
      { stand_file_id: 1, stand_file_name: 'Malzeme Listesi' },
      { stand_file_id: 3, stand_file_name: 'Elektrik Projesi' },
      { stand_file_id: 4, stand_file_name: 'Yangın Önlemleri' },
    ],
  },
  {
    id: 4, fuarId: null, standTypeId: 5, standTypeName: 'Ahşap',
    hMin: 3, hMax: 6, aciklama: 'Ahşap, 3–6 m — statik hesap',
    standFileIds: [1, 2, 3],
    standFiles: [
      { stand_file_id: 1, stand_file_name: 'Malzeme Listesi' },
      { stand_file_id: 2, stand_file_name: 'Statik Hesap' },
      { stand_file_id: 3, stand_file_name: 'Elektrik Projesi' },
    ],
  },
  {
    id: 5, fuarId: null, standTypeId: 2, standTypeName: 'Sistem Stand',
    hMin: null, hMax: null, aciklama: 'Sistem stant — belge istenmiyor',
    standFileIds: [], standFiles: [],
  },
];
let _mockNextId = 6;

/**
 * ⚠️ ProjectController'ın belge-kural YAZMA uçları gövdeyi bir zarfa sarıyor:
 *   { message, result, data: {...} }
 * OKUMA ucu (GET /belge-kural) ise ÇIPLAK BİR DİZİ dönüyor. (Teyit: fuar 298,
 * 2026-08-25 — POST/PUT/PUT-dosyalar hepsi zarflı, GET zarfsız.) Zarfı burada
 * soyuyoruz ki çağıran her zaman kaydın kendisini görsün; `data` yoksa (uç
 * sonradan zarfı bırakırsa) gövdenin kendisi dönüyor.
 */
function unwrap(body) {
  return body && typeof body === 'object' && 'data' in body && 'result' in body ? body.data : body;
}

function parseError(error) {
  const data = error.response?.data;
  const message =
    typeof data === 'string' ? data : data?.message ?? error.message ?? i18n.t("services:belgeKuralService.bilinmeyenHata");
  const err = new Error(message);
  err.status = error.response?.status ?? null;
  if (data && typeof data === 'object') err.data = data;
  throw err;
}

/**
 * İki yarı-açık aralık kesişiyor mu — hMax HARİÇ olduğu için `<` / `>` kullanılır,
 * `<=` değil. Backend zaten 409 ile koruyor; bu, kullanıcıya kaydetmeden ÖNCE
 * söyleyebilmek için (aynı mantığın FE kopyası).
 */
export function rangesOverlap(a, b) {
  const aMin = a.hMin ?? Number.NEGATIVE_INFINITY;
  const aMax = a.hMax ?? Number.POSITIVE_INFINITY;
  const bMin = b.hMin ?? Number.NEGATIVE_INFINITY;
  const bMax = b.hMax ?? Number.POSITIVE_INFINITY;
  return aMin < bMax && bMin < aMax;
}

/**
 * `target` aralığının TAMAMI `covers` aralıklarının birleşimiyle örtülüyor mu
 * (yarı-açık, null uç = sınırsız). Fuar kurallarının bir global kuralı tamamen mi
 * yoksa kısmen mi geçersiz kıldığını söylemek için: [0, 4) + [4, ∞) → [3, 6)'yı
 * tamamen örter; [0, 4) tek başına örtmez.
 */
export function rangeCoveredBy(target, covers) {
  const lo = (v) => (v == null ? Number.NEGATIVE_INFINITY : Number(v));
  const hi = (v) => (v == null ? Number.POSITIVE_INFINITY : Number(v));
  const tMin = lo(target.hMin);
  const tMax = hi(target.hMax);
  const parts = (covers || [])
    .map((c) => [Math.max(lo(c.hMin), tMin), Math.min(hi(c.hMax), tMax)])
    .filter(([a, b]) => a < b)
    // -Infinity - -Infinity = NaN; çıkarma yerine karşılaştırma.
    .sort((x, y) => (x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : 0));
  let cursor = tMin;
  for (const [a, b] of parts) {
    if (a > cursor) return false; // boşluk
    if (b > cursor) cursor = b;
  }
  return cursor >= tMax;
}

/** "[0,00 – 4,00) m" / "Her yükseklik" gibi okunur bir aralık etiketi. */
export function formatRange(rule) {
  const { hMin, hMax } = rule || {};
  if (hMin == null && hMax == null) return i18n.t("services:belgeKuralService.herYukseklik");
  const fmt = (v) => Number(v).toFixed(2).replace('.', ',');
  if (hMin == null) return i18n.t("services:belgeKuralService.mAlti", { fmt: fmt(hMax) });
  if (hMax == null) return i18n.t("services:belgeKuralService.mVeUstu", { fmt: fmt(hMin) });
  return `${fmt(hMin)} – ${fmt(hMax)} m`;
}

/**
 * Bir fuarın kuralları. fuarId geçilmezse GLOBAL kurallar (fuarId = null) döner.
 * GET /projects/belge-kural?fuarId={id}
 */
export async function getBelgeKurallari(fuarId = null, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockBelgeKural.filter((r) =>
      fuarId == null ? r.fuarId == null : Number(r.fuarId) === Number(fuarId));
  }
  try {
    const res = await axios.get(`${BASE}/belge-kural`, {
      params: fuarId == null ? {} : { fuarId },
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * Kural + dosya listesini birlikte oluştur.
 * 409 → aynı (fuar, stand tipi) için çakışan aralık; gövde çakışılan kuralı adlandırır.
 * POST /projects/belge-kural
 */
export async function createBelgeKural(body, { forceLive = false } = {}) {
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const payload = {
    fuarId: num(body.fuarId),
    standTypeId: Number(body.standTypeId),
    hMin: num(body.hMin),
    hMax: num(body.hMax),
    aciklama: body.aciklama?.trim() || null,
    standFileIds: (body.standFileIds || []).map(Number),
  };
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const clash = _mockBelgeKural.find((r) =>
      Number(r.standTypeId) === payload.standTypeId &&
      String(r.fuarId ?? '') === String(payload.fuarId ?? '') &&
      rangesOverlap(r, payload));
    if (clash) {
      const err = new Error(`Bu aralık mevcut bir kuralla çakışıyor: ${formatRange(clash)}`);
      err.status = 409; err.data = clash;
      throw err;
    }
    const created = { id: _mockNextId++, ...payload, standTypeName: null, standFiles: [] };
    _mockBelgeKural.push(created);
    return created;
  }
  try {
    // Tek çağrı yeter — POST gövdedeki standFileIds'i kaydeder ve dönen kayıtta
    // hidratlanmış `files` ile birlikte verir (teyit: fuar 298, 2026-08-25).
    const res = await axios.post(`${BASE}/belge-kural`, payload, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Kuralı düzenle. ⚠️ Gövdedeki fuarId/standTypeId YOK SAYILIR — bir kuralı başka
 * bir fuara/tipe TAŞIMAK için sil ve yeniden oluştur. Taşımayı bir düzenleme olarak
 * yapmak, çakışmama kontrolünün yapılacağı kümeyi değiştirir ve o yeniden kontrolü
 * gizlerdi.
 * PUT /projects/belge-kural/{id}
 */
export async function updateBelgeKural(id, body, { forceLive = false } = {}) {
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const payload = {
    hMin: num(body.hMin),
    hMax: num(body.hMax),
    aciklama: body.aciklama?.trim() || null,
  };
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const idx = _mockBelgeKural.findIndex((r) => Number(r.id) === Number(id));
    if (idx === -1) throw new Error('Kural bulunamadı');
    const merged = { ..._mockBelgeKural[idx], ...payload };
    const clash = _mockBelgeKural.find((r) =>
      Number(r.id) !== Number(id) &&
      Number(r.standTypeId) === Number(merged.standTypeId) &&
      String(r.fuarId ?? '') === String(merged.fuarId ?? '') &&
      rangesOverlap(r, merged));
    if (clash) {
      const err = new Error(`Bu aralık mevcut bir kuralla çakışıyor: ${formatRange(clash)}`);
      err.status = 409; err.data = clash;
      throw err;
    }
    _mockBelgeKural[idx] = merged;
    return merged;
  }
  try {
    const res = await axios.put(`${BASE}/belge-kural/${id}`, payload, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Sadece dosya listesini değiştir — gövde ÇIPLAK BİR DİZİ: [1, 3, 5].
 * PUT /projects/belge-kural/{id}/dosyalar
 */
export async function setBelgeKuralDosyalar(id, standFileIds, { forceLive = false } = {}) {
  const ids = (standFileIds || []).map(Number);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const idx = _mockBelgeKural.findIndex((r) => Number(r.id) === Number(id));
    if (idx === -1) throw new Error('Kural bulunamadı');
    _mockBelgeKural[idx] = { ..._mockBelgeKural[idx], standFileIds: ids, standFiles: [] };
    return _mockBelgeKural[idx];
  }
  try {
    const res = await axios.put(`${BASE}/belge-kural/${id}/dosyalar`, ids, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Soft delete. Her zaman 200.
 * ⚠️ Silmek, bu kurala göre ÇÖZÜLMÜŞ projelere geri dönmez — requiredFileIds bir
 * anlık görüntüdür. Değişikliği mevcut projelere itmek için reresolve çağırın
 * (projectService.js: reresolveProject / reresolveFuar).
 * DELETE /projects/belge-kural/{id}
 */
export async function deleteBelgeKural(id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    _mockBelgeKural = _mockBelgeKural.filter((r) => Number(r.id) !== Number(id));
    return { deleted: true };
  }
  try {
    const res = await axios.delete(`${BASE}/belge-kural/${id}`, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Bir proje için hangi belgelerin geçerli olduğu + 5 seviyenin HANGİSİNİN kazandığı.
 * `belgeKuralId` doluysa kazanan bir belge_kural satırıdır — linkleyin, adminin
 * cevabı değiştirmek için düzenleyeceği satır odur.
 * `judgedHeight` aralıkların test edildiği yüksekliktir (kabul edilen, yoksa beyan
 * edilen). `height` parametresi ne-olurdu senaryosu içindir.
 * ⚠️ `standHeightId` verilmezse iki _PSS seviyesi (FAIR_PSS/GLOBAL_PSS) HİÇ çözülemez ve
 *    cevap gerçeği eksik söyleyebilir (NONE) — uç tipten/standdan bandı TÜRETMİYOR.
 *    Sistem Ataması öncesi aday bantla çağırmak = "bu bantla hangi belgeler istenecek".
 * GET /projects/required-files/effective
 */
export async function getEffectiveRequiredFiles({ projectId = null, standId = null, standTypeId = null, standHeightId = null, height = null, fuarId = null, forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const h = height == null ? null : Number(height);
    // Mock ata zincirini YÜRÜMÜYOR (tip ağacı bu dosyada yok) — sadece birebir tip.
    // Fuar kuralı (fuarId verilmediyse herhangi bir fuarınki — eski mock davranışı)
    // globalin önünde; global mock satırları eklenince seviye etiketi yanlış olmasın.
    const hits = (r) =>
      Number(r.standTypeId) === Number(standTypeId) &&
      (h == null || ((r.hMin == null || h >= r.hMin) && (r.hMax == null || h < r.hMax)));
    const fair = _mockBelgeKural.find((r) =>
      r.fuarId != null && (fuarId == null || Number(r.fuarId) === Number(fuarId)) && hits(r));
    const global = fair ? null : _mockBelgeKural.find((r) => r.fuarId == null && hits(r));
    const match = fair || global;
    return match
      ? {
        level: fair ? REQUIRED_FILES_LEVEL.FAIR_KURAL : REQUIRED_FILES_LEVEL.GLOBAL_KURAL,
        belgeKuralId: match.id, judgedHeight: h, files: match.standFiles,
      }
      : { level: REQUIRED_FILES_LEVEL.NONE, belgeKuralId: null, judgedHeight: h, files: [] };
  }
  try {
    const params = {};
    if (projectId != null) params.projectId = projectId;
    if (standId != null) params.standId = standId;
    if (standTypeId != null) params.standTypeId = standTypeId;
    if (standHeightId != null) params.standHeightId = standHeightId;
    if (height != null) params.height = height;
    if (fuarId != null) params.fuarId = fuarId;
    const res = await axios.get(`${BASE}/required-files/effective`, { params, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}
